
// stores/profile.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApi } from '@/composables/useApi'

interface UserProfile {
    id: number
    email: string
    login: string
    user_type: string
    is_active: boolean
    is_deleted: boolean
    is_archive: boolean
    is_suspended: boolean
    created_at: string
    last_login: string | null
    slug: string
    code: string | null
    personne: any | null // Peut être null selon votre API
    /** Slug de l'étudiant (auth/me) — à utiliser pour receiver_id du Notification Service */
    etudiant?: { slug: string; [key: string]: any } | null
    groups: Array<{
        id: number
        name: string
        permissions: Array<{
            id: number
            name: string
            codename: string
            content_type: {
                id: number
                app_label: string
                model: string
            }
        }>
    }>
    user_permissions: any[]
}

export const useProfileStore = defineStore('profile', () => {
    const profile = ref<UserProfile | null>(null)
    const loading = ref(false)
    const error = ref<string | null>(null)

    // Computed properties
    const fullName = computed(() => {
        if (!profile.value) return ''
        // Si personne existe, utiliser ses données, sinon utiliser l'email
        if (profile.value.personne) {
            const fullName = `${profile.value.personne.prenom || ''} ${profile.value.personne.nom || ''}`.trim()
            // Ne retourner que si le nom n'est pas vide
            if (fullName) return fullName
        }
        // Fallback sur l'email ou login si personne est null, mais pas "Utilisateur"
        return profile.value.email || profile.value.login || ''
    })

    const userTypeLabel = computed(() => {
        if (!profile.value) return ''
        const types: Record<string, string> = {
            'etudiant': 'Étudiant',
            'personnel': 'Personnel',
            'admin': 'Administrateur',
            'superadmin': 'Super Administrateur',
            'enseignant': 'Enseignant',
            'secretaire': 'Secrétaire'
        }
        return types[profile.value.user_type] || profile.value.user_type
    })



    const avatarUrl = computed(() => {
        const {buildAssetUrl} = useDjangoAssets()
        return profile.value?.personne?.avatar?buildAssetUrl(profile.value?.personne?.avatar) : null
    })

    // Computed pour les permissions
    const userType = computed(() => {
        return profile.value?.user_type || ''
    })

    const isStudent = computed(() => {
        if (!profile.value?.groups) return false
        return profile.value.groups.some((group: any) => group.name === 'Etudiant')
    })

    const isAdmin = computed(() => {
        if (!profile.value?.groups) return false
        return profile.value.groups.some((group: any) =>
            ['Admin', 'Administrateur', 'Superviseur'].includes(group.name)
        )
    })

    const isPersonnel = computed(() => {
        if (!profile.value?.groups) return false
        // Exclure le superadmin de la logique restrictive du personnel
        if (profile.value.user_type === 'superadmin') return false
        return profile.value.groups.some((group: any) =>
            ['Personnel', 'Enseignant', 'Secrétaire', 'Comptable', 'RH'].includes(group.name)
        )
    })

    const isEnseignant = computed(() => {
        if (!profile.value?.groups) return false
        return profile.value.groups.some((group: any) => group.name === 'Enseignant')
    })

    const isComptable = computed(() => {
        if (!profile.value?.groups) return false
        return profile.value.groups.some((group: any) => group.name === 'Comptable')
    })

    const isRH = computed(() => {
        if (!profile.value?.groups) return false
        return profile.value.groups.some((group: any) => group.name === 'RH')
    })

    const isSuperAdmin = computed(() => {
        return profile.value?.user_type === 'superadmin'
    })

    // Actions
    const fetchProfile = async () => {
        loading.value = true
        error.value = null

        try {
            // Appel des fonctions de login avec paramètres en dur
            // await testLoginFunctions()

            const { apiDjangoAuth } = useApi()
            const response: any = await apiDjangoAuth('/auth/me/')
            profile.value = response
        } catch (err: any) {
            error.value = err?.message || 'Erreur lors du chargement du profil'
            // console.error('Erreur lors du chargement du profil:', err)
        } finally {
            loading.value = false
        }
    }

    const updateProfile = async (data: Partial<UserProfile>) => {
        loading.value = true
        error.value = null

        try {
            const { apiDjangoAuth } = useApi()
            const response: any = await apiDjangoAuth('/auth/me/', {
                method: 'PATCH',
                body: data
            })

            if (profile.value) {
                profile.value = { ...profile.value, ...response }
            }
        } catch (err: any) {
            error.value = err?.message || 'Erreur lors de la mise à jour'
            throw err
        } finally {
            loading.value = false
        }
    }

    const clearProfile = () => {
        profile.value = null
        error.value = null
        loading.value = false
    }

    // Fonction de test pour appeler loginEncrypted et loginBis avec des paramètres en dur
    const testLoginFunctions = async () => {
        try {

            // Paramètres en dur
            const hardcodedCredentials = {
                login: "myditdev@gmail.com",
                password: "myditdev"
            }

            // Import des fonctions depuis useAuth
            const { loginEncrypted, loginBis } = useAuth()

            // Appel de loginEncrypted avec paramètres en dur
            // console.log('Appel de loginEncrypted...')
            // const cipher = await loginEncrypted(hardcodedCredentials.login, hardcodedCredentials.password)
            // console.log('Résultat loginEncrypted:', cipher)
            // Afficher EXACTEMENT l'objet demandé

            // Appel de loginBis avec paramètres en dur
            const resultBis = await loginBis(hardcodedCredentials)
            // Afficher le token décrypté (access) retourné par authenticateUserBis
            if (resultBis && resultBis.token) {
            }

            return { "cipher": 1, resultBis }
        } catch (err: any) {
            // console.error('Erreur lors du test des fonctions de login:', err)
            error.value = err?.message || 'Erreur lors du test des fonctions de login'
            throw err
        }
    }

    return {
        // State
        profile,
        loading,
        error,

        // Computed
        fullName,
        userTypeLabel,
        avatarUrl,
        userType,
        isStudent,
        isAdmin,
        isPersonnel,
        isEnseignant,
        isComptable,
        isRH,
        isSuperAdmin,

        // Actions
        fetchProfile,
        updateProfile,
        clearProfile,
        testLoginFunctions
    }
})

